// face-api.js loader + embedding utility.
// Runs 100% in the browser — photos never leave the device for face matching.
// Models are lazy-loaded once from /face-models/ (bundled in /public).
import * as faceapi from "face-api.js";

const MODEL_URL = "/face-models";

let _loaded = false;
let _loadingPromise = null;

export async function loadFaceModels() {
  if (_loaded) return;
  if (_loadingPromise) return _loadingPromise;
  _loadingPromise = (async () => {
    // Prefer GPU; fall back to CPU on devices without WebGL (still works, slower).
    try {
      await faceapi.tf.setBackend("webgl");
      await faceapi.tf.ready();
    } catch (_) {
      try {
        await faceapi.tf.setBackend("cpu");
        await faceapi.tf.ready();
      } catch (_) { /* let net loading surface the real error */ }
    }
    await Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ]);
    _loaded = true;
  })();
  try {
    return await _loadingPromise;
  } catch (e) {
    _loadingPromise = null; // allow retry on next call instead of caching failure
    throw e;
  }
}

// Detect all faces in an image element / canvas and return their 128-dim embeddings.
export async function extractEmbeddings(imgEl) {
  await loadFaceModels();
  const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 });
  const results = await faceapi
    .detectAllFaces(imgEl, opts)
    .withFaceLandmarks(true) // useTinyModel
    .withFaceDescriptors();
  return results.map((r) => Array.from(r.descriptor));
}

// Detect the single best face (highest score) — used for the selfie step.
export async function extractSingleEmbedding(imgEl) {
  const r = await extractSingleFace(imgEl);
  return r ? r.descriptor : null;
}

// Like extractSingleEmbedding but also returns the detection box so callers
// can crop a small avatar from the selfie.
export async function extractSingleFace(imgEl) {
  await loadFaceModels();
  const opts = new faceapi.TinyFaceDetectorOptions({ inputSize: 416, scoreThreshold: 0.3 });
  const result = await faceapi
    .detectSingleFace(imgEl, opts)
    .withFaceLandmarks(true)
    .withFaceDescriptor();
  if (!result) return null;
  const { x, y, width, height } = result.detection.box;
  return { descriptor: Array.from(result.descriptor), box: { x, y, width, height } };
}

// Crop the face box (with generous margin) from a source canvas into a small
// square JPEG data-URL used as the person's icon in the People strip.
export function cropFaceAvatar(sourceCanvas, box, size = 144) {
  const margin = 0.45;
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  const side = Math.max(box.width, box.height) * (1 + margin * 2);
  const sx = Math.max(0, cx - side / 2);
  const sy = Math.max(0, cy - side / 2);
  const sw = Math.min(sourceCanvas.width - sx, side);
  const sh = Math.min(sourceCanvas.height - sy, side);
  const out = document.createElement("canvas");
  out.width = size;
  out.height = size;
  out.getContext("2d").drawImage(sourceCanvas, sx, sy, sw, sh, 0, 0, size, size);
  return out.toDataURL("image/jpeg", 0.82);
}

// Euclidean distance between two 128-dim descriptors.
// face-api's recommended threshold for "same person" is ~0.55–0.6.
export function euclideanDistance(a, b) {
  let sum = 0;
  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i];
    sum += d * d;
  }
  return Math.sqrt(sum);
}

// Best (minimum) distance from selfie to any face in a photo.
export function bestDistance(selfieDesc, photoDescs) {
  if (!Array.isArray(photoDescs) || photoDescs.length === 0) return Infinity;
  let best = Infinity;
  for (const d of photoDescs) {
    const dist = euclideanDistance(selfieDesc, d);
    if (dist < best) best = dist;
  }
  return best;
}

// LocalStorage cache — keyed per album so revisits are instant.
const CACHE_PREFIX = "meyecam.face.v1.";
export function getCache(slug) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + slug);
    return raw ? JSON.parse(raw) : {};
  } catch (_) {
    return {};
  }
}
export function saveCache(slug, cache) {
  try {
    localStorage.setItem(CACHE_PREFIX + slug, JSON.stringify(cache));
  } catch (_) {
    // storage full — silently ignore
  }
}

// Load an image element from a URL (with anonymous CORS for /thumb endpoints).
export function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
}

// Wait until a <video> element is actually producing frames (iOS Safari can
// take a beat after play()). Returns false on timeout.
export async function waitForVideoReady(video, timeoutMs = 4000) {
  const start = Date.now();
  while (video && video.videoWidth === 0 && Date.now() - start < timeoutMs) {
    await new Promise((r) => setTimeout(r, 100));
  }
  return !!(video && video.videoWidth > 0);
}
